import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import type { BodyWithChildren } from "./sceneActions/bodyUtils";

interface PanelState {
  expandedBodies: Record<string, boolean>;
  loadingPreset: boolean;
  loadingProgress: number;
  loadingMessage: string;
  loadingPhase: "idle" | "xml" | "structure" | "meshes" | "complete";
  sceneGraphOpen: boolean;
  filePanelOpen: boolean;
  animationPanelOpen: boolean;
  materialEditorPanelOpen: boolean;
  lightsPanelOpen: boolean;
  circleDetectionOpen: boolean;
  historyPanelOpen: boolean;
  runtimeDebugOpen: boolean;
  ikTargetPanelOpen: boolean;
  objectLibraryPanelOpen: boolean;
  runtimeControlsOpen: boolean;
  physicsPanelOpen: boolean;
  actuatorControlsOpen: boolean;
  warmstartDatasetOpen: boolean;
  behaviorEditorOpen: boolean;
  policyRolloutOpen: boolean;
  trainingMetricsOpen: boolean;
  trainingPipelineOpen: boolean;
  octoTrainingOpen: boolean;
  meshEditorOpen: boolean;
  sceneInventoryOpen: boolean;
  modelCheckOpen: boolean;
  massPropertiesOpen: boolean;
  measurementsOpen: boolean;
  visibilitySettingsOpen: boolean;
  terrainPanelOpen: boolean;
  xmlSourcePanelOpen: boolean;
  cameraProbeOpen: boolean;
  sensorsOpen: boolean;
  shortcutsPanelOpen: boolean;
  getUpdatesPanelOpen: boolean;
  sendFeedbackPanelOpen: boolean;
  aboutPanelOpen: boolean;
  gremlinsPanelOpen: boolean;
  objectLibraryRequestedTab: string | null;
  materialEditorFocusMaterialId: string | null;
}

type PanelFlagKey =
  | "sceneGraphOpen"
  | "filePanelOpen"
  | "animationPanelOpen"
  | "materialEditorPanelOpen"
  | "lightsPanelOpen"
  | "circleDetectionOpen"
  | "historyPanelOpen"
  | "runtimeDebugOpen"
  | "ikTargetPanelOpen"
  | "objectLibraryPanelOpen"
  | "runtimeControlsOpen"
  | "physicsPanelOpen"
  | "actuatorControlsOpen"
  | "warmstartDatasetOpen"
  | "behaviorEditorOpen"
  | "policyRolloutOpen"
  | "trainingMetricsOpen"
  | "trainingPipelineOpen"
  | "octoTrainingOpen"
  | "meshEditorOpen"
  | "sceneInventoryOpen"
  | "modelCheckOpen"
  | "massPropertiesOpen"
  | "measurementsOpen"
  | "visibilitySettingsOpen"
  | "terrainPanelOpen"
  | "xmlSourcePanelOpen"
  | "cameraProbeOpen"
  | "sensorsOpen"
  | "shortcutsPanelOpen"
  | "getUpdatesPanelOpen"
  | "sendFeedbackPanelOpen"
  | "aboutPanelOpen"
  | "gremlinsPanelOpen";

interface PanelActions {
  setExpandedBodies: (expandedBodies: Record<string, boolean>) => void;
  toggleExpandedBody: (bodyName: string) => void;
  setLoadingPreset: (loadingPreset: boolean) => void;
  setLoadingProgress: (loadingProgress: number) => void;
  setLoadingMessage: (loadingMessage: string) => void;
  setLoadingPhase: (loadingPhase: PanelState["loadingPhase"]) => void;
  setSceneGraphOpen: (sceneGraphOpen: boolean) => void;
  setFilePanelOpen: (filePanelOpen: boolean) => void;
  setAnimationPanelOpen: (animationPanelOpen: boolean) => void;
  setMaterialEditorPanelOpen: (materialEditorPanelOpen: boolean) => void;
  setLightsPanelOpen: (lightsPanelOpen: boolean) => void;
  setCircleDetectionOpen: (circleDetectionOpen: boolean) => void;
  setHistoryPanelOpen: (historyPanelOpen: boolean) => void;
  setRuntimeDebugOpen: (runtimeDebugOpen: boolean) => void;
  setIkTargetPanelOpen: (ikTargetPanelOpen: boolean) => void;
  setObjectLibraryPanelOpen: (open: boolean) => void;
  setRuntimeControlsOpen: (runtimeControlsOpen: boolean) => void;
  setPhysicsPanelOpen: (physicsPanelOpen: boolean) => void;
  setActuatorControlsOpen: (actuatorControlsOpen: boolean) => void;
  setWarmstartDatasetOpen: (warmstartDatasetOpen: boolean) => void;
  setBehaviorEditorOpen: (behaviorEditorOpen: boolean) => void;
  setPolicyRolloutOpen: (policyRolloutOpen: boolean) => void;
  setTrainingMetricsOpen: (trainingMetricsOpen: boolean) => void;
  setTrainingPipelineOpen: (trainingPipelineOpen: boolean) => void;
  setOctoTrainingOpen: (octoTrainingOpen: boolean) => void;
  setMeshEditorOpen: (meshEditorOpen: boolean) => void;
  setSceneInventoryOpen: (sceneInventoryOpen: boolean) => void;
  setModelCheckOpen: (modelCheckOpen: boolean) => void;
  setMassPropertiesOpen: (massPropertiesOpen: boolean) => void;
  setMeasurementsOpen: (measurementsOpen: boolean) => void;
  setVisibilitySettingsOpen: (visibilitySettingsOpen: boolean) => void;
  setTerrainPanelOpen: (terrainPanelOpen: boolean) => void;
  setXmlSourcePanelOpen: (xmlSourcePanelOpen: boolean) => void;
  setCameraProbeOpen: (cameraProbeOpen: boolean) => void;
  setSensorsOpen: (sensorsOpen: boolean) => void;
  setShortcutsPanelOpen: (shortcutsPanelOpen: boolean) => void;
  setGetUpdatesPanelOpen: (getUpdatesPanelOpen: boolean) => void;
  setSendFeedbackPanelOpen: (sendFeedbackPanelOpen: boolean) => void;
  setAboutPanelOpen: (aboutPanelOpen: boolean) => void;
  setGremlinsPanelOpen: (gremlinsPanelOpen: boolean) => void;
  setObjectLibraryRequestedTab: (tab: string | null) => void;
  setMaterialEditorFocusMaterialId: (materialId: string | null) => void;
  setPanelOpen: (panel: PanelFlagKey, open: boolean) => void;
  expandAllBodies: (bodies: BodyWithChildren[]) => void;
  collapseAllBodies: (bodies: BodyWithChildren[]) => void;
  resetPanel: () => void;
}

export type PanelStore = PanelState & { actions: PanelActions };

export const usePanelStore = create<PanelStore>((set) => ({
  expandedBodies: {},
  loadingPreset: false,
  loadingProgress: 0,
  loadingMessage: "",
  loadingPhase: "idle",
  sceneGraphOpen: false,
  filePanelOpen: false,
  animationPanelOpen: false,
  materialEditorPanelOpen: false,
  lightsPanelOpen: false,
  circleDetectionOpen: false,
  historyPanelOpen: false,
  runtimeDebugOpen: false,
  ikTargetPanelOpen: false,
  objectLibraryPanelOpen: false,
  runtimeControlsOpen: false,
  physicsPanelOpen: false,
  actuatorControlsOpen: false,
  warmstartDatasetOpen: false,
  behaviorEditorOpen: false,
  policyRolloutOpen: false,
  trainingMetricsOpen: false,
  trainingPipelineOpen: false,
  octoTrainingOpen: false,
  meshEditorOpen: false,
  sceneInventoryOpen: false,
  modelCheckOpen: false,
  massPropertiesOpen: false,
  measurementsOpen: false,
  visibilitySettingsOpen: false,
  terrainPanelOpen: false,
  xmlSourcePanelOpen: false,
  cameraProbeOpen: false,
  sensorsOpen: false,
  shortcutsPanelOpen: false,
  getUpdatesPanelOpen: false,
  sendFeedbackPanelOpen: false,
  aboutPanelOpen: false,
  gremlinsPanelOpen: false,
  objectLibraryRequestedTab: null,
  materialEditorFocusMaterialId: null,

  actions: {
    setExpandedBodies: (expandedBodies) => set({ expandedBodies }),
    toggleExpandedBody: (bodyName) =>
      set((state) => ({
        expandedBodies: {
          ...state.expandedBodies,
          [bodyName]: !state.expandedBodies[bodyName],
        },
      })),

    setLoadingPreset: (loadingPreset) => set({ loadingPreset }),
    setLoadingProgress: (loadingProgress) => set({ loadingProgress }),
    setLoadingMessage: (loadingMessage) => set({ loadingMessage }),
    setLoadingPhase: (loadingPhase) => set({ loadingPhase }),

    setSceneGraphOpen: (sceneGraphOpen) => set({ sceneGraphOpen }),
    setFilePanelOpen: (filePanelOpen) => set({ filePanelOpen }),
    setAnimationPanelOpen: (animationPanelOpen) => set({ animationPanelOpen }),
    setMaterialEditorPanelOpen: (materialEditorPanelOpen) => set({ materialEditorPanelOpen }),
    setLightsPanelOpen: (lightsPanelOpen) => set({ lightsPanelOpen }),
    setCircleDetectionOpen: (circleDetectionOpen) => set({ circleDetectionOpen }),
    setHistoryPanelOpen: (historyPanelOpen) => set({ historyPanelOpen }),
    setRuntimeDebugOpen: (runtimeDebugOpen) => set({ runtimeDebugOpen }),
    setIkTargetPanelOpen: (ikTargetPanelOpen) => set({ ikTargetPanelOpen }),
    setObjectLibraryPanelOpen: (objectLibraryPanelOpen) => set({ objectLibraryPanelOpen }),
    setRuntimeControlsOpen: (runtimeControlsOpen) => set({ runtimeControlsOpen }),
    setPhysicsPanelOpen: (physicsPanelOpen) => set({ physicsPanelOpen }),
    setActuatorControlsOpen: (actuatorControlsOpen) => set({ actuatorControlsOpen }),
    setWarmstartDatasetOpen: (warmstartDatasetOpen) => set({ warmstartDatasetOpen }),
    setBehaviorEditorOpen: (behaviorEditorOpen) => set({ behaviorEditorOpen }),
    setPolicyRolloutOpen: (policyRolloutOpen) => set({ policyRolloutOpen }),
    setTrainingMetricsOpen: (trainingMetricsOpen) => set({ trainingMetricsOpen }),
    setTrainingPipelineOpen: (trainingPipelineOpen) => set({ trainingPipelineOpen }),
    setOctoTrainingOpen: (octoTrainingOpen) => set({ octoTrainingOpen }),
    setMeshEditorOpen: (meshEditorOpen) => set({ meshEditorOpen }),
    setSceneInventoryOpen: (sceneInventoryOpen) => set({ sceneInventoryOpen }),
    setModelCheckOpen: (modelCheckOpen) => set({ modelCheckOpen }),
    setMassPropertiesOpen: (massPropertiesOpen) => set({ massPropertiesOpen }),
    setMeasurementsOpen: (measurementsOpen) => set({ measurementsOpen }),
    setVisibilitySettingsOpen: (visibilitySettingsOpen) => set({ visibilitySettingsOpen }),
    setTerrainPanelOpen: (terrainPanelOpen) => set({ terrainPanelOpen }),
    setXmlSourcePanelOpen: (xmlSourcePanelOpen) => set({ xmlSourcePanelOpen }),
    setCameraProbeOpen: (cameraProbeOpen) => set({ cameraProbeOpen }),
    setSensorsOpen: (sensorsOpen) => set({ sensorsOpen }),
    setShortcutsPanelOpen: (shortcutsPanelOpen) => set({ shortcutsPanelOpen }),
    setGetUpdatesPanelOpen: (getUpdatesPanelOpen) => set({ getUpdatesPanelOpen }),
    setSendFeedbackPanelOpen: (sendFeedbackPanelOpen) => set({ sendFeedbackPanelOpen }),
    setAboutPanelOpen: (aboutPanelOpen) => set({ aboutPanelOpen }),
    setGremlinsPanelOpen: (gremlinsPanelOpen) => set({ gremlinsPanelOpen }),
    setObjectLibraryRequestedTab: (objectLibraryRequestedTab) => set({ objectLibraryRequestedTab }),
    setMaterialEditorFocusMaterialId: (materialEditorFocusMaterialId) =>
      set({ materialEditorFocusMaterialId }),
    setPanelOpen: (panel, open) => set(() => ({ [panel]: open }) as Pick<PanelState, PanelFlagKey>),

    expandAllBodies: (bodies) => {
      const expandAll = (nodes: BodyWithChildren[], expanded: Record<string, boolean> = {}) => {
        nodes.forEach((node) => {
          expanded[node.name] = true;
          if (node.children && node.children.length > 0) {
            expandAll(node.children, expanded);
          }
        });
        return expanded;
      };
      set({ expandedBodies: expandAll(bodies) });
    },

    collapseAllBodies: (bodies) => {
      const collapseAll = (nodes: BodyWithChildren[], expanded: Record<string, boolean> = {}) => {
        nodes.forEach((node) => {
          expanded[node.name] = false;
          if (node.children && node.children.length > 0) {
            collapseAll(node.children, expanded);
          }
        });
        return expanded;
      };
      set({ expandedBodies: collapseAll(bodies) });
    },

    resetPanel: () =>
      set({
        expandedBodies: {},
        loadingPreset: false,
        loadingProgress: 0,
        loadingMessage: "",
        loadingPhase: "idle",
      }),
  },
}));

export const usePanelActions = () => usePanelStore((state) => state.actions);
export const useSceneGraphOpen = () => usePanelStore((state) => state.sceneGraphOpen);
export const useObjectLibraryPanelOpen = () =>
  usePanelStore((state) => state.objectLibraryPanelOpen);
export const useObjectLibraryRequestedTab = () =>
  usePanelStore((state) => state.objectLibraryRequestedTab);
export const useMaterialEditorFocusMaterialId = () =>
  usePanelStore((state) => state.materialEditorFocusMaterialId);
export const useLoadingPreset = () => usePanelStore((state) => state.loadingPreset);
export const useFileLoadingStatus = () =>
  usePanelStore(
    useShallow((state) => ({
      loadingProgress: state.loadingProgress,
      loadingMessage: state.loadingMessage,
      loadingPhase: state.loadingPhase,
    }))
  );
